import { useState } from 'react'
import {
  Box, Button, Chip, Tooltip, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material'
import { Lock, LockOpen, TaskAlt } from '@mui/icons-material'
import { useApi } from '../../hooks/useApi'
import { useAuth } from '../../hooks/useAuth'
import { approvalStamp, confirmText } from './approval'

// FAR-61: approve / unapprove a Production Plan from the Planning Board header.
// Mark: "On the main plan view/edit screen. Approved by the planner. Approval
// should stamp date/time and user id." "Mark as Production Plan" is now the
// same thing as Approved.
//
// FAR-83 §6: each action is confirmed in a modal (approve the plan as of that
// moment, or cancel; remove the approval, or cancel). Once approved the button
// reads "Remove Approval". The stamp is the user's ID, name and the date.

export const APPROVED = 'Approved' // must match models.PlanStatusApproved
const APPROVAL_ROLES = ['planner', 'approver', 'admin'] // must match handlers.approvalRoles

export function canApprove(user) {
  const roles = String(user?.roles || '').split(',').map((r) => r.trim())
  return roles.some((r) => APPROVAL_ROLES.includes(r))
}

export default function ApprovalControl({ plan, plantCode, onChange }) {
  const { post } = useApi()
  const { user } = useAuth()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [confirming, setConfirming] = useState(null) // 'approve' | 'unapprove' | null

  if (!plan?.id) return null
  const approved = plan.status === APPROVED
  const allowed = canApprove(user)

  const act = async (action) => {
    setBusy(true)
    setError(null)
    try {
      const updated = await post(`/plans/${plan.id}/${action}`, { plant: plantCode })
      onChange?.(updated)
      setConfirming(null)
    } catch (e) {
      setError(e.message || (action === 'approve' ? 'Could not approve the plan' : 'Could not remove the approval'))
      setConfirming(null)
    } finally {
      setBusy(false)
    }
  }

  const stamp = approved ? approvalStamp(plan) : null
  const text = confirming ? confirmText(confirming, plan) : null

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {approved && (
        <Tooltip title={stamp || 'Approved'}>
          <Chip icon={<Lock sx={{ fontSize: 16 }} />} label="Approved" color="primary" size="small" data-testid="approved-chip" />
        </Tooltip>
      )}
      <Tooltip title={allowed ? '' : 'Only planners, approvers and admins can approve'}>
        <span>
          {approved ? (
            <Button size="small" variant="outlined" startIcon={<LockOpen />} disabled={!allowed || busy}
              onClick={() => setConfirming('unapprove')} data-testid="remove-approval">
              Remove Approval
            </Button>
          ) : (
            <Button size="small" variant="contained" startIcon={<TaskAlt />} disabled={!allowed || busy}
              onClick={() => setConfirming('approve')} data-testid="approve">
              Approve
            </Button>
          )}
        </span>
      </Tooltip>
      {error && <Alert severity="error" sx={{ py: 0 }} onClose={() => setError(null)}>{error}</Alert>}

      <Dialog open={!!confirming} onClose={() => !busy && setConfirming(null)} data-testid="approval-dialog"
        aria-labelledby="approval-dialog-title">
        {text && (
          <>
            <DialogTitle id="approval-dialog-title">{text.title}</DialogTitle>
            <DialogContent>
              <DialogContentText>{text.body}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirming(null)} disabled={busy}>Cancel</Button>
              <Button variant="contained" color={confirming === 'unapprove' ? 'error' : 'primary'} disabled={busy}
                onClick={() => act(confirming)} data-testid="approval-confirm">
                {text.confirm}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  )
}
